import { addDays } from "@/lib/calendars";

/*
 * The days the date picker draws and the bounds it keeps (ACL-577). A day is `YYYY-MM-DD` throughout,
 * as a date field stores it: it sorts as the days do and has no clock, so no timezone or DST moves it.
 */

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "2026-10-24" from a year, a month (1 to 12) and a day of the month. */
export function dayOf(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** The year, month (1 to 12) and day of the month of a `YYYY-MM-DD`. */
export function partsOf(day: string): { year: number; month: number; day: number } {
  const [, y = "0", m = "1", d = "1"] = DAY_RE.exec(day) ?? [];
  return { year: Number(y), month: Number(m), day: Number(d) };
}

/** Whether `text` is a day that exists, as `YYYY-MM-DD` ("2026-02-30" is not). */
export function isDay(text: string): boolean {
  if (!DAY_RE.test(text)) return false;
  const { year, month, day } = partsOf(text);
  return month >= 1 && month <= 12 && day >= 1 && day <= daysIn(year, month);
}

/** How many days a month has. */
export function daysIn(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** The same day `n` months on, or that month's last day when it is shorter (Jan 31 and one month is
 *  Feb 28), so stepping a month never skips one. */
export function shiftMonths(day: string, n: number): string {
  const { year, month, day: d } = partsOf(day);
  const index = year * 12 + (month - 1) + n;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  return dayOf(y, m, Math.min(d, daysIn(y, m)));
}

/** The six weeks a month is drawn on, Sunday first: 42 days from the Sunday on or before its 1st. Always
 *  six, so the picker keeps its height from month to month. */
export function monthGrid(year: number, month: number): string[] {
  const first = dayOf(year, month, 1);
  const start = addDays(first, -new Date(Date.UTC(year, month - 1, 1)).getUTCDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** Today on this computer's clock. */
export function localToday(now = new Date()): string {
  return dayOf(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Whether a day may be picked: on or after `min` and on or before `max`, each when there is one. */
export function allowed(day: string, min?: string, max?: string): boolean {
  return (!min || day >= min) && (!max || day <= max);
}

/** The day nearest `day` that may be picked. */
export function clampDay(day: string, min?: string, max?: string): string {
  if (min && day < min) return min;
  if (max && day > max) return max;
  return day;
}
